import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getPermissions } from "store/userSlice";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import {
  fetchBudgetSettings,
  getBudgetSettings,
  getBudgetSettingsError,
  updateError
} from "store/budgetSettingsSlice";
import { api } from "helpers/axios";
import { errorMessage, formatMoney, formatNumberTo } from "helpers/common";
import * as Sentry from "@sentry/react";
import { toast } from "helpers/toast";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import BudgetForm from "./BudgetForm";
import BudgetGroupForm from "./BudgetGroupForm";
import FixInfo from "components/Calculation/FixInfo";
import { tableResize } from "helpers/tableResize";
import TextTruncate from "components/ui/TextTruncate";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import ExportForm from "components/Forms/Export";
import { helpTextIcon } from "helpers/helpText";
import ErrorAlert from "components/ui/ErrorAlert";
import hasPermissionTo from "helpers/permission";
import { useTranslation } from "translation/useTranslation";
import { Button, Modal, Select, Field, Input } from "components/ui";

function GroupRow({ group, budgets, handleDetachFromGroup, handleShowDelete, handleChangeSelectedBudget, handleCalculateBudgetDistribution, enableBatchUpdate, batchUpdateCheckedList, handleBatchUpdateChecked, resizedCount, showExport, flatTableFields, editableTranslate, translate }) {
  return <tr className="budget-group-tr table-row no-select">
    {
      enableBatchUpdate && <td className="v-middle td-checkbox">
        <div className="boxed-budget-group-div">
          <span className="child-span d-flex justify-content-center"><input type="checkbox" checked={batchUpdateCheckedList.includes('group-' + group.hash_id)} onChange={(e) => handleBatchUpdateChecked(e, 'group-' + group.hash_id)} disabled={batchUpdateCheckedList[0] && !batchUpdateCheckedList[0].startsWith('group-')} data-testid={`batch-update-check-group-${group.hash_id}`} /></span>
          {
            budgets.map((b, i) => <span key={i} className="child-span d-flex justify-content-center"><input type="checkbox" checked={batchUpdateCheckedList.includes('budget-' + b.hash_id)} onChange={(e) => handleBatchUpdateChecked(e, 'budget-' + b.hash_id)} disabled={batchUpdateCheckedList[0] && !batchUpdateCheckedList[0].startsWith('budget-')} data-testid={`batch-update-check-budget-${b.hash_id}`} /></span>)
          }
        </div>
      </td>
    }
    {
      <td className="v-middle budget-name-td">
        <div className="boxed-budget-group-div">
          <TextTruncate text={group.name} className="child-span font-weight-bold" resizedCount={resizedCount} />
          {
            budgets.map((b, i) => <span key={i} className="child-span">
              <TextTruncate text={b.name} className="ml-4" resizedCount={resizedCount} />
            </span>)
          }
        </div>
      </td>
    }
    {
      <td className="v-middle">
        <div className="boxed-budget-group-div">
          <span className="child-span">&nbsp;</span>
          {
            budgets.map((b, i) => <span key={i} className="child-span">
              <TextTruncate text={b.calculation_model?.name} resizedCount={resizedCount} />
            </span>)
          }
        </div>
      </td>
    }
    {
      <td className="v-middle days-col">
        <div className="boxed-budget-group-div">
          <span className="child-span">&nbsp;</span>
          {
            budgets.map((b, i) => b.days ? <span key={i} className="child-span">
              <TextTruncate text={b.days} resizedCount={resizedCount} />
            </span> : <span key={i} className="child-span">&nbsp;</span>)
          }
        </div>
      </td>
    }
    {
      <td className="v-middle days-col">
        <div className="boxed-budget-group-div">
          <span className="child-span">&nbsp;</span>
          {
            budgets.map((b, i) => b.months ? <span key={i} className="child-span">
              <TextTruncate text={b.months} resizedCount={resizedCount} />
            </span> : <span key={i} className="child-span">&nbsp;</span>)
          }
        </div>
      </td>
    }
    {
      !showExport && <td className="v-middle days-col">
        <div className="boxed-budget-group-div">
          {/* if group.distribution_status === 'completed' && group.distribution_calculation_factor, use this icon */}
          {/* work time, we show a clock, number of employees, we show person, payfactor, we show money icon */}
          {
            group.distribution_status === 'completed' && group.distribution_calculation_factor ? <>
              {/* if two icon, make them inline */}
              <div className="d-flex align-items-center justify-content-center" data-tooltip-id={`group-distribution-calculation-factor-${group.hash_id}`} data-tooltip-content={group.distribution_calculation_factor}>
                {
                  group.budget_distributions.includes('work_hours') && <i className="bi bi-clock p-1"></i>
                }
                {
                  group.budget_distributions.includes('number_of_employees') && <i className="bi bi-person-circle p-1"></i>
                }
                {
                  group.budget_distributions.includes('pay_factor') && <i className="bi bi-coin p-1"></i>
                }
                {
                  group.budget_distributions.includes('annual_compensation') && <i className="bi bi-bank p-1"></i>
                }
              </div>
              <ReactTooltip id={`group-distribution-calculation-factor-${group.hash_id}`} place="top" effect="solid" className="text-left"></ReactTooltip>
            </> : <span className="child-span">&nbsp;</span>
          }
          {
            budgets.map((b, i) => (group.distribution_status === 'completed' && b.budget_calculation_factor) ? <span key={i} className="child-span">
              <TextTruncate text={b.budget_calculation_factor} resizedCount={resizedCount} />
            </span> : <span key={i} className="child-span">&nbsp;</span>)
          }
        </div>
      </td>
    }
    {
      <td className="v-middle text-right">
        <div className="boxed-budget-group-div">
          <span className="child-span" data-tooltip-id={`group-distribution-calculated-${group.hash_id}`}>{group.budget_amount ? <TextTruncate text={formatMoney(group.budget_amount)} resizedCount={resizedCount} /> : ''}</span>
          <ReactTooltip id={`group-distribution-calculated-${group.hash_id}`} place="top" effect="solid" className="text-left" clickable>
            {Array.isArray(group.distribution_calculation_log) ? (
              <ul className="mb-0 p-3">
                {group.distribution_calculation_log.map((log, idx) => <li key={idx}>{typeof log === 'string' ? log : editableTranslate(log.translation_key, log.fallback, log.values)}</li>)}
              </ul>
            ) : null}
          </ReactTooltip>
          {
            budgets.map((b, i) => <span key={i} className="child-span">
              {
                group.distribution_status === 'completed' ? (
                  b.amount ? <>
                    <TextTruncate text={formatMoney(b.amount)} className="d-inline-block v-middle" resizedCount={resizedCount} /><sup className="peb-provisional-mark" data-tooltip-id="my-tooltip-1">*</sup>
                  </> : <>&nbsp;</>
                ) : (
                  // calculating or error
                  group.distribution_status === 'calculating' ? (<>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span className="ml-1">{editableTranslate('budget_setting.calculation_status.calculating')}</span>
                  </>) : group.distribution_status === 'failed' ? (<>
                    <i className="bi bi-exclamation-circle text-danger" data-tooltip-id={`group-distribution-error-${group.hash_id}`}></i>
                    <span className="ml-1">{editableTranslate('budget_setting.calculation_status.failed')}</span>
                    <ReactTooltip id={`group-distribution-error-${group.hash_id}`} place="top" effect="solid" className="text-left" clickable>
                      {Array.isArray(group.distribution_calculation_log) ? group.distribution_calculation_log.map((log, idx) => <div key={idx}>{typeof log === 'string' ? log : editableTranslate(log.translation_key, log.fallback, log.values)}</div>) : null}
                    </ReactTooltip>
                  </>) : null
                )
              }</span>)
          }
        </div>
      </td>
    }
    <td className="v-middle text-center">
      <div className="boxed-budget-group-div">
        <span data-testid="group-actions" className="child-span">
          <Button data-testid="edit-button" variant="ghost" size="inline" icon="bi-pencil-square" className="mr-1" disabled={enableBatchUpdate} onClick={() => handleChangeSelectedBudget('group', group.hash_id)} />
          <Button variant="ghost" size="inline" icon="bi-box-arrow-in-right" className="mr-1 invisible" tabIndex={-1} />
          <Button data-testid="calculate-button" variant="ghost" size="inline" icon="bi-calculator" className="mr-1" disabled={enableBatchUpdate || group.distribution_status === 'calculating'} onClick={() => handleCalculateBudgetDistribution(group.hash_id)} />
          <Button data-testid="delete-button" variant="ghost" size="inline" tone="danger" icon="bi-trash" disabled={enableBatchUpdate} className={Array.isArray(group.budget_settings) && group.budget_settings.length === 0 ? '' : 'invisible'} onClick={() => handleShowDelete(group)} />
        </span>
        {
          budgets.map((b, i) => <span data-testid={`budget-actions-${b.hash_id}`} key={i} className="child-span">
            <Button data-testid="edit-button" variant="ghost" size="inline" icon="bi-pencil-square" className="mr-1" disabled={enableBatchUpdate} onClick={() => handleChangeSelectedBudget('budget', b.hash_id)} />
            <Button data-testid="detach-button" variant="ghost" size="inline" icon="bi-box-arrow-right" className="mr-1" disabled={enableBatchUpdate} onClick={() => handleDetachFromGroup(b)} />
            <Button variant="ghost" size="inline" icon="bi-calculator" className="mr-1 invisible" tabIndex={-1} />
            <Button variant="ghost" size="inline" icon="bi-trash" className="invisible" tabIndex={-1} />
          </span>)
        }
      </div>
    </td>
  </tr>
}

export default function Budgets() {
  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();


  const { translate, editableTranslate } = useTranslation();

  const error = useSelector(getBudgetSettingsError);
  const budgets = useSelector(getBudgetSettings);

  const permissions = useSelector(getPermissions);

  const { helps } = useOutletContext();

  const [budgetsData, setBudgetsData] = useState(null);

  const [groups, setGroups] = useState(null);
  const [groupsData, setGroupsData] = useState(null);
  const [groupOptions, setGroupOptions] = useState([]);

  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinData, setJoinData] = useState({});
  const [joinError, setJoinError] = useState(null);
  const [joinMixError, setJoinMixError] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState({});

  const [selectedBudget, setSelectedBudget] = useState(null);

  const [totalBudgetAmount, setTotalBudgetAmount] = useState(0);

  const [enableBatchUpdate, setEnableBatchUpdate] = useState(false);
  const [batchUpdateCheckedList, setBatchUpdateCheckedList] = useState([]);

  const [fixMode, setFixMode] = useState(false);
  const [fixData, setFixData] = useState(null);

  const theadRef = useRef(null);

  const [sortCols, setSortCols] = useState([['name', 'asc']]);
  const [sortPinCol, setSortPinCol] = useState(null);

  const [resizedCount, setResizedCount] = useState(0);
  const [triggerColWidth, setTriggerColWidth] = useState(0);

  const [showExport, setShowExport] = useState(false);
  const [tableFields, setTableFields] = useState([]);

  const [exportError, setExportError] = useState(null);

  const tableRef = useRef(null);

  const flatTableFields = useMemo(() => {
    const obj = {};
    for (const f of tableFields) {
      obj[f.key] = f;
    }
    return obj;
  }, [tableFields]);

  const objSortCols = useMemo(() => {
    const obj = {};

    for (const sc of sortCols) {
      obj[sc[0]] = sc[1];
    }

    return obj;
  }, [sortCols]);

  useEffect(() => {
    if (!groups || !budgets) {
      return;
    }

    let totalAmount = 0;
    budgets.forEach(b => { if (!b.budget_group) { totalAmount += parseFloat(b.amount || 0) } })
    groups.forEach(g => { if (g.budget_amount) { totalAmount += parseFloat(g.budget_amount || 0) } })

    setTotalBudgetAmount(totalAmount);

  }, [groups, budgets]);

  useEffect(() => {

    // Set an interval to fetch budgets every 5 seconds
    const interval = setInterval(() => {
      fetchAllBudgets(true);
    }, 5000);

    fetchAllBudgets();
    fetchTemplate();

    const theadEl = theadRef.current;
    if (!theadEl) return;

    const handleScrollTable = () => {
      const parentEl = theadEl.parentElement;
      const coordinates = parentEl.getBoundingClientRect();
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;
      if (coordinates.y < navHeight) {
        theadEl.style.transform = `translate3d(0, ${-coordinates.y + navHeight}px, 0)`;
      } else {
        theadEl.style.transform = 'translate3d(0,0,0)';
      }
    };
    window.addEventListener('scroll', handleScrollTable);

    // Clear the interval on component unmount
    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', handleScrollTable);
    };
  }, []);

  useEffect(() => {
    if (searchParams.get('source') === 'calculation') {
      const budgetHashId = searchParams.get('budget_hash_id');
      const budgetName = searchParams.get('budget_name');
      const budgetGroupHashId = searchParams.get('budget_group_hash_id');
      const errorKey = searchParams.get('error_key');

      if (budgetGroupHashId) {
        setFixData({ type: 'group', hash_id: budgetGroupHashId, source_hash_id: budgetHashId, budget_name: budgetName, error_key: errorKey });
      } else if (budgetHashId) {
        setFixData({ type: 'budget', hash_id: budgetHashId, source_hash_id: budgetHashId, budget_name: budgetName, error_key: errorKey });

        setSelectedBudget({ type: 'budget', id: budgetHashId });
      }
      setFixMode(true);
    } else {
      setFixMode(false);
      setFixData(null);
    }
  }, [searchParams]);

  const minColWidth = {
    action: 120
  };

  const maxColWidth = {
    name: 300,
    calculation_model: 300,
    days: 150,
    months: 150
  };

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    if (!enableBatchUpdate) {
      tableResize(table, 'budgets', false, minColWidth, maxColWidth, setResizedCount);
    }
  }, [budgets, enableBatchUpdate, selectedBudget, triggerColWidth, showExport]);

  useEffect(() => {
    if (budgets) {
      const sortedBudgets = [...budgets];
      sortBudgets(sortedBudgets, sortCols);
      setBudgetsData(sortedBudgets);
    } else {
      setBudgetsData(null);
    }

    if (groups) {
      const sortedGroups = [...groups];
      sortGroups(sortedGroups, sortCols);
      setGroupsData(sortedGroups);
    } else {
      setGroupsData(null);
    }
  }, [budgets, groups, sortCols]);

  useEffect(() => {
    setResizedCount(c => c + 1);
  }, [showExport, selectedBudget, batchUpdateCheckedList]);

  const fetchAllBudgets = (background = false) => {
    dispatch(fetchBudgetSettings({ background }));
    fetchGroups(background);
  };

  const fetchGroups = async (background = false) => {
    try {
      const { data, status } = await api.get(`/settings/budget-groups`, { meta: { background } });
      if (status === 200) {
        setGroups(data.data);

        const options = [];
        data.data.forEach(d => {
          options.push({
            value: d.hash_id,
            label: d.name
          });
        });
        setGroupOptions(options);
      }
    } catch (err) {
      dispatch(updateError(errorMessage(err, 'fetch budget groups')));
      Sentry.captureException(err);
    }
  };

  const handleShowJoin = (budget) => {
    setJoinError(null);
    setJoinMixError(false);
    setSelectedBudget(null);
    setJoinData({
      budget: budget.hash_id,
      budget_group: budget.budget_group?.hash_id,
      budget_group_amount: budget.budget_group?.budget_amount,
      budget_calculation_model: budget?.calculation_model
    });
    setShowJoinModal(true);
  };

  const handleHideJoin = () => {
    setShowJoinModal(false);
    setJoinData({});
  };

  const handleChangeGroup = (val) => {
    setJoinError(null);
    setJoinMixError(false);

    if (val) {
      const group = groups.filter(g => g.hash_id === val.value)[0];

      let allWithoutBudget = true;

      for (const b of group?.budget_settings) {
        if ((joinData?.budget_calculation_model?.is_without_budget && !b?.calculation_model?.is_without_budget) || (!joinData?.budget_calculation_model?.is_without_budget && b?.calculation_model?.is_without_budget)) {
          allWithoutBudget = false;
          break;
        }
      }

      setJoinData({
        ...joinData,
        budget_group: val.value,
        budget_group_amount: group?.budget_amount
      });

      if (!allWithoutBudget) {
        setJoinError(translate('calculation_settings.mix_error'));
        setJoinMixError(true);
        return;
      }
    } else {
      setJoinData({
        ...joinData,
        budget_group: null,
        budget_group_amount: null
      });
    }
  };

  const handleDetachFromGroup = async (budget) => {
    setJoinError(null);
    setJoinMixError(false);
    setSelectedBudget(null);

    const nextJoinData = {
      budget: budget.hash_id,
      budget_group: null,
      budget_group_amount: null,
      budget_calculation_model: budget?.calculation_model,
    };

    setJoinData(nextJoinData);

    await handleSaveJoin(nextJoinData, 'detach-budget-group');
  };

  const handleSaveJoin = async (joinDataOverride, type = 'attach-budget-group') => {
    const data = joinDataOverride || joinData;

    if (type === 'attach-budget-group' && !data.budget_group) return;

    setJoinError(null);

    try {
      const { status } = await api.post(`/settings/budgets/${data.budget}/${type}`, {
        budget_group_hash_id: data.budget_group
      });
      if (status === 200) {
        fetchAllBudgets();
        handleHideJoin();
        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      // the row detach has no modal open, so its error goes to the page alert
      if (type === 'detach-budget-group') dispatch(updateError(errorMessage(err, 'detach budget setting from group')));
      else setJoinError(errorMessage(err, 'update budget setting group'));
      Sentry.captureException(err);
    }
  };

  const handleShowDelete = (budget) => {
    setSelectedBudget(null);
    setShowDeleteModal(true);
    setDeleteData({
      type: (budget?.calculation_model ? 'budgets' : 'budget-groups'),
      budget: budget.hash_id
    });
  };

  const handleHideDelete = () => {
    setShowDeleteModal(false);
    setDeleteData({});
  };

  const handleYesDelete = async () => {
    try {
      const { status } = await api.delete(`/settings/${deleteData.type}/${deleteData.budget}`);
      if (status === 200) {
        toast.success(translate('form.success.saved'));
        fetchAllBudgets();
        handleHideDelete();
      }
    } catch (err) {
      handleHideDelete();
      dispatch(updateError(errorMessage(err, 'delete budget setting group')));
      Sentry.captureException(err);
    }
  };

  const handleChangeSelectedBudget = (type, id = null) => {
    setShowExport(false);

    if (!type) setSelectedBudget(null);
    else setSelectedBudget({ type, id });
  };

  const handleCalculateBudgetDistribution = async (budget_group_hash_id = null) => {
    try {
      const body = {};
      if (budget_group_hash_id) body.budget_group_hash_id = budget_group_hash_id;

      const { status } = await api.post(`/settings/budget-groups/calculate-distribution`, body);
      if (status === 200) {
        fetchAllBudgets();
        toast.success(translate('budget_setting.calculate_budget_distribution_started'));
      }
    } catch (err) {
      dispatch(updateError(errorMessage(err, 'calculate budget distribution')));
      Sentry.captureException(err);
    }
  };

  const handleEnableBatchUpdate = () => {
    setShowExport(false);

    setBatchUpdateCheckedList([]);
    setEnableBatchUpdate(!enableBatchUpdate);

    setSelectedBudget(null);
  };

  const handleBatchUpdateChecked = (e, val) => {
    if (e.target.checked) {
      const newBatchUpdateCheckedList = [...batchUpdateCheckedList];
      newBatchUpdateCheckedList.push(val);
      setBatchUpdateCheckedList(newBatchUpdateCheckedList);
    } else {
      setBatchUpdateCheckedList(batchUpdateCheckedList.filter(v => v !== val));
    }
  };

  const handleFixGoBack = (e) => {
    e.preventDefault();

    navigate({ pathname: '/calculation/preliminary-check', search: `?hash_id=${fixData?.source_hash_id}` });
  };

  const handleFixCancel = (e) => {
    e.preventDefault();

    setSelectedBudget(null);
    setSearchParams({});
  };

  const handleChangeSortCol = (field) => {
    const newSortCols = [...sortCols];
    let found = false;
    for (let i = 0; i < newSortCols.length; i++) {
      if (newSortCols[i][0] === field) {
        newSortCols[i][1] = (newSortCols[i][1] === 'asc' ? 'desc' : 'asc');
        found = true;
        break;
      }
    }
    if (!found) {
      if (newSortCols.length > 1 || !sortPinCol) newSortCols.pop(); // max 2, so if > 1 or no pin, we remove the last one first before push
      newSortCols.push([field, 'asc']);
    }
    setSortCols(newSortCols);
  };

  const handleSetSortPinCol = (field) => {
    if (sortPinCol === field) {
      setSortPinCol(null);
      setSortCols([sortCols[0]]);
    } else {
      setSortPinCol(field);
      setSortCols(sortCols.filter(sc => sc[0] === field));
    }
  };

  useEffect(() => {
    if (!sortPinCol) setTriggerColWidth(p => p + 1);
  }, [sortCols]);

  const sortBudgets = (arr, sortCols) => {
    arr.sort((a, b) => {
      for (const [key, direction] of sortCols) {
        let left, right;

        switch (key) {
          case 'name':
            left = (a?.name || '').toLowerCase();
            right = (b?.name || '').toLowerCase();
            break;
          case 'calculation_model_name':
            left = (a?.calculation_model?.name || '').toLowerCase();
            right = (b?.calculation_model?.name || '').toLowerCase();
            break;
          case 'budget_amount':
            left = (!a?.calculation_model?.is_without_budget && a.amount !== null) ? +formatNumberTo(a.amount, '.') : -1;
            right = (!b?.calculation_model?.is_without_budget && b.amount !== null) ? +formatNumberTo(b.amount, '.') : -1;
            break;
          case 'budget_days':
            left = a['days'] ? +a['days'] : -1;
            right = b['days'] ? +b['days'] : -1;
            break;
          case 'budget_months':
            left = a['months'] ? +a['months'] : -1;
            right = b['months'] ? +b['months'] : -1;
            break;
          default:
            left = 0;
            right = 0;
        }

        const multiplier = direction === 'asc' ? 1 : -1;
        if (left < right) return -1 * multiplier;
        if (left > right) return 1 * multiplier;
      }
      return 0;
    });
  };

  const sortGroups = (arr, sortCols) => {
    arr.sort((a, b) => {
      for (const [key, direction] of sortCols) {
        let left, right;

        switch (key) {
          case 'name':
            left = (a?.name || '').toLowerCase();
            right = (b?.name || '').toLowerCase();
            break;
          case 'budget_amount':
            left = a['budget_amount'] ? +formatNumberTo(a['budget_amount'], '.') : -1;
            right = b['budget_amount'] ? +formatNumberTo(b['budget_amount'], '.') : -1;
            break;
          default:
            left = 0;
            right = 0;
        }

        const multiplier = direction === 'asc' ? 1 : -1;
        if (left < right) return -1 * multiplier;
        if (left > right) return 1 * multiplier;
      }
      return 0;
    });
  };

  const fetchTemplate = async () => {
    try {
      const { data, status } = await api.get(`/exports/templates/standard_budgets`);
      if (status === 200) {
        const columns = data.columns.map(f => ({ ...f, displayed: true }));
        setTableFields(columns);
      }
    } catch (err) {
      setExportError(translate('report.template_not_found', 'Die Berichtsvorlage konnte nicht gefunden werden.'));
      Sentry.captureException(err);
    }
  };

  const handleExport = (e) => {
    e?.currentTarget?.blur();
    setSelectedBudget(null);
    setShowExport(!showExport);
  };

  const getLabel = (key, fallback) => {
    const sKey = ('budget_setting.' + key.replace('budget_setting.', '')).replaceAll('.', '-');
    const help = (helps && helps.filter(h => h.key === sKey)[0]) || null;

    return <>
      {editableTranslate(key, fallback)}
      {help && helpTextIcon(sKey, help.short_text)}
    </>
  };

  return (
    <>
      <div className={`col-lg col-md-7 ${(selectedBudget || batchUpdateCheckedList.length > 0) ? 'section-col-mid has-form-right' : (showExport ? 'section-col-mid has-export-side' : 'section-col-right')} d-flex`}>
        <div className={`bg-white w-100 p-4 content-card`}>
          <div className={`d-flex ${!fixMode ? 'justify-content-between' : ''}`}>
            {
              !fixMode && <>
                <div>
                  <h4 className="mb-0">{editableTranslate('global.menu.settings.budgets')}</h4>
                </div>
                <div className="d-flex flex-wrap justify-content-end gap-2">
                  {
                    !selectedBudget && <Button data-testid="calculate-button" variant="secondary" icon="bi-calculator" onClick={() => handleCalculateBudgetDistribution()} disabled={enableBatchUpdate || (groupsData && groupsData.some(g => g.distribution_status === 'calculating'))}>
                      {getLabel('budget_setting.calculate_budget_distribution')}
                    </Button>
                  }
                  <Button data-testid="batch-update-button" variant="secondary" onClick={handleEnableBatchUpdate}>{
                    enableBatchUpdate ?
                      editableTranslate('budget_setting.cancel_batch_update') :
                      getLabel('budget_setting.batch_update')
                  }</Button>
                  {
                    (!selectedBudget || selectedBudget?.id !== null) && <Button data-testid="add-budget-button" variant="secondary" icon="bi-plus" onClick={() => handleChangeSelectedBudget('budget')} disabled={enableBatchUpdate}>
                      {getLabel('budget_setting.add_budget')}
                    </Button>
                  }
                  {
                    (!selectedBudget || selectedBudget?.id !== null) && <Button data-testid="add-budget-group-button" variant="secondary" icon="bi-plus" onClick={() => handleChangeSelectedBudget('group')} disabled={enableBatchUpdate}>
                      {getLabel('budget_setting.add_budget_group')}
                    </Button>
                  }
                  {
                    hasPermissionTo('reports.budgets.*', permissions) && <Button data-testid="export-button" variant="secondary" icon="bi-file-earmark-arrow-down" onClick={handleExport} disabled={enableBatchUpdate}>{getLabel('global.export', 'Export')}</Button>
                  }
                </div>
              </>
            }
          </div>

          {
            fixMode && <div>
              <div className="text-right mb-3 d-flex justify-content-end gap-2">
                <Button data-testid="back-button" variant="secondary" icon="bi-arrow-left" onClick={handleFixGoBack}>{editableTranslate('calculation_overview.go_back')}</Button>
                <Button data-testid="cancel-button" variant="secondary" onClick={handleFixCancel}>{editableTranslate('button.cancel')}</Button>
              </div>

              <FixInfo budgetName={fixData?.budget_name} errorKey={fixData?.error_key} />
            </div>
          }

          {
            error && <div className="mt-3">
              <ErrorAlert error={error} onClose={() => dispatch(updateError(null))} />
            </div>
          }

          <div className="mt-3">
            <div className="mt-3">
              <div className="table-responsive has-custom-scrollbar">
                <table ref={tableRef} className="table budget-overview-table">
                  <thead ref={theadRef} className="sticky-top">
                    <tr>
                      {enableBatchUpdate && <th></th>}
                      {
                        <th scope="col" className="cursor-pointer">
                          <span className="float-right floating-icons">
                            <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['name'] ? (objSortCols['name'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('name')} data-testid="sort-name" />
                            {
                              objSortCols['name'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'name' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('name')} data-testid="sort-pin-name" />
                            }
                          </span>
                          <span data-testid="header-name" className="th-span" onClick={() => handleChangeSortCol('name')}>
                            {getLabel('budget_setting.budget_name')}
                            <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                            <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                          </span>
                          {
                            !enableBatchUpdate && <div className="th-resize-div" data-field={'name'}></div>
                          }
                        </th>
                      }
                      {
                        <th scope="col" className="cursor-pointer">
                          <span className="float-right floating-icons">
                            <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['calculation_model_name'] ? (objSortCols['calculation_model_name'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('calculation_model_name')} data-testid="sort-calculation-model-name" />
                            {
                              objSortCols['calculation_model_name'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'calculation_model_name' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('calculation_model_name')} data-testid="sort-pin-calculation-model-name" />
                            }
                          </span>
                          <span data-testid="header-calculation-model-name" className="th-span" onClick={() => handleChangeSortCol('calculation_model_name')}>
                            {getLabel('budget_setting.calculation_model')}
                            <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                            <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                          </span>
                          {
                            !enableBatchUpdate && <div className="th-resize-div" data-field={'calculation_model'}></div>
                          }
                        </th>
                      }
                      {
                        <th scope="col" className={`cursor-pointer days-col ${batchUpdateCheckedList.length > 0 || selectedBudget ? 'panel-open' : ''}`}>
                          <span className="float-right floating-icons">
                            <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['budget_days'] ? (objSortCols['budget_days'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('budget_days')} data-testid="sort-budget-days" />
                            {
                              objSortCols['budget_days'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'budget_days' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('budget_days')} data-testid="sort-pin-budget-days" />
                            }
                          </span>
                          <span data-testid="header-budget-days" className="th-span" onClick={() => handleChangeSortCol('budget_days')}>
                            {getLabel('budget_setting.days')}
                            <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                            <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                          </span>
                          {
                            !enableBatchUpdate && <div className="th-resize-div" data-field={'days'}></div>
                          }
                        </th>
                      }
                      {
                        <th scope="col" className={`cursor-pointer days-col ${batchUpdateCheckedList.length > 0 || selectedBudget ? 'panel-open' : ''}`}>
                          <span className="float-right floating-icons">
                            <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['budget_months'] ? (objSortCols['budget_months'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('budget_months')} data-testid="sort-budget-months" />
                            {
                              objSortCols['budget_months'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'budget_months' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('budget_months')} data-testid="sort-pin-budget-months" />
                            }
                          </span>
                          <span data-testid="header-budget-months" className="th-span" onClick={() => handleChangeSortCol('budget_months')}>
                            {getLabel('budget_setting.budget_months')}
                            <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                            <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                          </span>
                          {
                            !enableBatchUpdate && <div className="th-resize-div" data-field={'months'}></div>
                          }
                        </th>
                      }
                      {
                        !showExport && <th scope="col" className={`days-col ${batchUpdateCheckedList.length > 0 || selectedBudget ? 'panel-open' : ''}`}>
                          {getLabel('budget_setting.calculation_factor', 'Factor')}
                          {
                            !enableBatchUpdate && <div className="th-resize-div" data-field={'calculation_factor'}></div>
                          }
                        </th>
                      }
                      {
                        <th scope="col" className="cursor-pointer text-right">
                          <span className="float-right floating-icons">
                            <Button variant="ghost" size="inline" className="p-0 mr-1" icon={`bi-bold ${objSortCols['budget_amount'] ? (objSortCols['budget_amount'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('budget_amount')} data-testid="sort-budget-amount" />
                            {
                              objSortCols['budget_amount'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'budget_amount' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('budget_amount')} data-testid="sort-pin-budget-amount" />
                            }
                          </span>
                          <span data-testid="header-budget-amount" className="th-span" onClick={() => handleChangeSortCol('budget_amount')}>
                            {getLabel('budget_setting.budget_amount')}
                            <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                            <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                          </span>
                          {
                            !enableBatchUpdate && <div className="th-resize-div" data-field={'amount'}></div>
                          }
                        </th>
                      }
                      <th scope="col" className="text-center">
                        {getLabel('form.action')}
                        {
                          !enableBatchUpdate && <div className="th-resize-div" data-field={'action'}></div>
                        }
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      (!fixData || fixData?.type === 'budget') && Array.isArray(groupsData) && Array.isArray(budgetsData) && budgetsData.filter(b => !b.budget_group && (fixData?.hash_id ? b.hash_id === fixData.hash_id : true)).map((budget, index) => (
                        <tr key={index} className="table-row no-select" data-testid={`row-${budget?.hash_id}`}>
                          {enableBatchUpdate && <td className="td-checkbox"><input type="checkbox" checked={batchUpdateCheckedList.includes('budget-' + budget.hash_id)} onChange={(e) => handleBatchUpdateChecked(e, 'budget-' + budget.hash_id)} disabled={batchUpdateCheckedList[0] && !batchUpdateCheckedList[0].startsWith('budget-')} data-testid={`batch-update-check-budget-${budget.hash_id}`} /></td>}
                          {
                            <td><TextTruncate text={budget.name} resizedCount={resizedCount} /></td>
                          }
                          {
                            <td><TextTruncate text={budget.calculation_model?.name} resizedCount={resizedCount} /></td>
                          }
                          {
                            <td className={`days-col ${batchUpdateCheckedList.length > 0 || selectedBudget ? 'panel-open' : ''}`}>
                              <TextTruncate text={budget.days} resizedCount={resizedCount} />
                            </td>
                          }
                          {
                            <td className={`days-col ${batchUpdateCheckedList.length > 0 || selectedBudget ? 'panel-open' : ''}`}>
                              <TextTruncate text={budget.months} resizedCount={resizedCount} />
                            </td>
                          }
                          {
                            !showExport && <td className={`days-col ${batchUpdateCheckedList.length > 0 || selectedBudget ? 'panel-open' : ''}`}>
                              -
                            </td>
                          }
                          {
                            <td className="text-right">
                              {
                                budget?.calculation_model?.is_without_budget ? '' : (budget.amount !== null ? <TextTruncate text={formatMoney(budget.amount)} resizedCount={resizedCount} /> : '')
                              }
                            </td>
                          }
                          <td className="text-center red-dark font-weight-bold text-nowrap">
                            <Button data-testid="edit-button" variant="ghost" size="inline" icon="bi-pencil-square" className="mr-1" disabled={enableBatchUpdate} onClick={() => handleChangeSelectedBudget('budget', budget.hash_id)} />
                            <Button data-testid="join-button" variant="ghost" size="inline" icon="bi-box-arrow-in-right" className="mr-1" disabled={enableBatchUpdate} onClick={() => handleShowJoin(budget)} />
                            <Button variant="ghost" size="inline" icon="bi-calculator" className="mr-1 invisible" />
                            <Button data-testid="delete-button" variant="ghost" size="inline" tone="danger" icon="bi-trash" disabled={enableBatchUpdate} className={!budget.attached_to_balanced_scorecard ? '' : 'invisible'} onClick={() => handleShowDelete(budget)} />
                          </td>
                        </tr>
                      ))
                    }
                    {
                      (!fixData || fixData?.type === 'group') && Array.isArray(groupsData) && Array.isArray(budgetsData) && groupsData.filter(g => fixData?.hash_id ? g.hash_id === fixData.hash_id : true).map((group, index) => (
                        <Fragment key={index}>
                          <tr data-testid={`row-${group?.hash_id}`}>
                            <td colSpan={enableBatchUpdate ? 7 : 6} className="separator-td"></td>
                          </tr>
                          <GroupRow key={index} group={group} budgets={budgetsData ? budgetsData.filter(b => b.budget_group && b.budget_group.hash_id === group.hash_id) : []} handleDetachFromGroup={handleDetachFromGroup} handleShowDelete={handleShowDelete} handleChangeSelectedBudget={handleChangeSelectedBudget} handleCalculateBudgetDistribution={handleCalculateBudgetDistribution} enableBatchUpdate={enableBatchUpdate} batchUpdateCheckedList={batchUpdateCheckedList} handleBatchUpdateChecked={handleBatchUpdateChecked} resizedCount={resizedCount} showExport={showExport} flatTableFields={flatTableFields} editableTranslate={editableTranslate} translate={translate} />
                        </Fragment>
                      ))
                    }
                    {
                      (!fixData && Array.isArray(groupsData) && Array.isArray(budgetsData) && (groupsData.length > 0 || budgetsData.length > 0) && <tr>
                        <td colSpan={4 + (enableBatchUpdate ? 1 : 0) + (!showExport ? 1 : 0)}></td>
                        <td className="text-center" id="total_budget_amount">
                          {
                            <span className="float-right font-weight-bold">
                              {totalBudgetAmount ? formatMoney(totalBudgetAmount) : ''}
                            </span>
                          }
                        </td>
                        <td></td>
                      </tr>) || null
                    }
                    {
                      (Array.isArray(groupsData) && groupsData.length === 0 && Array.isArray(budgetsData) && budgetsData.length === 0) && <tr>
                        <td colSpan={6} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                      </tr>
                    }
                    {
                      (!groupsData || !budgetsData) && <tr>
                        <td colSpan={6}>
                          <div className="d-flex justify-content-center" data-testid="loading">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">Wird geladen...</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
              <TableScrollbar tableRef={tableRef} data={budgetsData} trigger={resizedCount} />
              {
                Array.isArray(groupsData) && Array.isArray(budgetsData) &&
                groupsData.some(g => g.distribution_status === 'completed' && budgetsData.some(b => b.budget_group?.hash_id === g.hash_id && b.amount)) &&
                <p className="peb-provisional-note mt-2 mb-0">
                  <span className="peb-provisional-mark">*</span> {editableTranslate('budget_setting.provisional_share_note', 'Anteil noch nicht endgültig - wird nach der nächsten Verteilung aktualisiert.')}
                </p>
              }
            </div>
          </div>
        </div>
        <Modal
          open={showJoinModal}
          title={editableTranslate('budget_setting.join_group')}
          onClose={handleHideJoin}
          testIdPrefix="join-"
          footer={<>
            <Button data-testid="cancel-button" variant="secondary" onClick={handleHideJoin}>{editableTranslate('button.cancel')}</Button>
            <Button data-testid="save-button" variant="primary" onClick={() => handleSaveJoin()} disabled={joinMixError || !joinData?.budget_group}>{editableTranslate('button.save')}</Button>
          </>}
        >
          {
            joinError && <ErrorAlert error={joinError} onClose={() => setJoinError(null)} />
          }
          <Field label={editableTranslate('budget_setting.budget_group')} className="peb-field-mb" required>
            <Select data-testid="budget-group-select" value={joinData?.budget_group ? groupOptions.filter(g => g.value === joinData.budget_group)[0] : ''} isClearable={true} options={groupOptions} onChange={handleChangeGroup} placeholder={translate('form.select_placeholder')} />
          </Field>
          <Field label={editableTranslate('budget_setting.budget_group_amount')} className="peb-field-mb" required>
            <Input data-testid="budget-group-amount-input" type="text" value={joinData?.budget_group_amount ? formatMoney(joinData?.budget_group_amount) : ''} readOnly />
          </Field>
        </Modal>
        <Modal
          open={showDeleteModal}
          danger
          title={deleteData?.type === 'budgets'
            ? editableTranslate('budget_setting.delete_budget_confirmation_title', 'Budgeteinstellung löschen?')
            : editableTranslate('budget_setting.delete_budget_group_confirmation_title', 'Budgetgruppe löschen?')}
          cancelLabel={editableTranslate('form.no')}
          okLabel={editableTranslate('form.delete.yes')}
          onClose={handleHideDelete}
          onConfirm={handleYesDelete}
          testIdPrefix="budgets-delete-"
        >
          {editableTranslate(deleteData?.type === 'budgets' ? 'budget_setting.delete_budget_confirmation' : 'budget_setting.delete_budget_group_confirmation')}
        </Modal>
      </div>

      <ReactTooltip
        id="my-tooltip-1"
        place="bottom"
        content={translate('tooltips.budget_setting.budget_amount_warning')}
      />

      {
        selectedBudget && <div className="col d-flex">
          {
            selectedBudget.type === 'budget' && <BudgetForm id={selectedBudget.id} handleChangeSelectedBudget={handleChangeSelectedBudget} fetchAllBudgets={fetchAllBudgets} />
          }
          {
            selectedBudget.type === 'group' && <BudgetGroupForm id={selectedBudget.id} handleChangeSelectedBudget={handleChangeSelectedBudget} fetchAllBudgets={fetchAllBudgets} />
          }
        </div>
      }

      {
        batchUpdateCheckedList.length > 0 && <div className="col d-flex">
          {
            !batchUpdateCheckedList[0].startsWith('group-') && <BudgetForm id={null} handleChangeSelectedBudget={handleChangeSelectedBudget} fetchAllBudgets={fetchAllBudgets} batchUpdateCheckedList={batchUpdateCheckedList} handleEnableBatchUpdate={handleEnableBatchUpdate} />
          }
          {
            batchUpdateCheckedList[0].startsWith('group-') && <BudgetGroupForm id={null} handleChangeSelectedBudget={handleChangeSelectedBudget} fetchAllBudgets={fetchAllBudgets} batchUpdateCheckedList={batchUpdateCheckedList} handleEnableBatchUpdate={handleEnableBatchUpdate} />
          }
        </div>
      }

      {
        showExport && <div className="col-lg col-md-2 section-col-right small-section min-height-after-navbar">
          <div className="bg-white w-100 p-4 content-card h-100">
            <div className="side-header">
              <h5 className="m-0 font-weight-bold">{editableTranslate('global.export', 'Bericht/Export')}</h5>
              <span className="cursor-pointer font-weight-bold balanced-scorecard-close" onClick={() => setShowExport(false)} data-testid="close-button">
                <i className="bi bi-x-lg"></i>
              </span>
            </div>
            <ExportForm templateKey="standard_budgets" tableFields={tableFields} otherData={{ totalRows: budgets?.length }} parentError={exportError} />
          </div>
        </div>
      }
    </>
  );
}
